import { Eye, EyeOff, RotateCcw, Square, Trash2 } from 'lucide-react';

import { cn } from '../lib/cn.js';
import { HIGHLIGHT_COLORS } from '../lib/highlight-node-reducer.js';
import { useUiStore } from '../state/ui-store.js';

export interface GraphStatusBarProps {
  readonly nodeCount: number | undefined;
  readonly edgeCount: number | undefined;
  readonly truncated: boolean;
  readonly layoutRunning: boolean;
  readonly selectedNodeLabel: string | null;
  readonly onStop: () => void;
  readonly onRestart: () => void;
}

export function GraphStatusBar({
  nodeCount,
  edgeCount,
  truncated,
  layoutRunning,
  selectedNodeLabel,
  onStop,
  onRestart,
}: GraphStatusBarProps): JSX.Element {
  const highlights = useUiStore((s) => s.highlights);
  const toggleVisible = useUiStore((s) => s.toggleHighlightsVisible);
  const clearHighlights = useUiStore((s) => s.clearHighlights);

  const totalHighlights =
    highlights.citation.length + highlights.tool.length + highlights.blast.length;
  const hasAny = totalHighlights > 0;

  return (
    <footer
      role="status"
      className="flex h-9 flex-none items-center gap-3 border-t border-border-subtle bg-deep/85 px-3 font-mono text-[11px] text-text-secondary backdrop-blur"
    >
      <span className="flex items-center gap-1.5">
        <span className="text-text-primary">{fmt(nodeCount)}</span>
        <span className="text-text-muted">nodes</span>
        <span className="text-border-default">·</span>
        <span className="text-text-primary">{fmt(edgeCount)}</span>
        <span className="text-text-muted">edges</span>
        {truncated && (
          <span className="ml-1 rounded-full border border-amber-500/30 bg-amber-500/15 px-1.5 text-[10px] text-amber-300">
            truncated
          </span>
        )}
      </span>

      <span className="h-4 w-px bg-border-subtle" aria-hidden />

      <Indicator running={layoutRunning} />
      <PillButton onClick={onStop} disabled={!layoutRunning} icon={<Square className="h-3 w-3" />}>
        Stop
      </PillButton>
      <PillButton onClick={onRestart} icon={<RotateCcw className="h-3 w-3" />}>
        Restart
      </PillButton>

      <span className="flex-1" />

      <HighlightCounts highlights={highlights} />
      <PillButton
        onClick={toggleVisible}
        disabled={!hasAny}
        icon={highlights.visible ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
        title={highlights.visible ? 'Hide AI highlights' : 'Show AI highlights'}
      >
        Highlights {highlights.visible ? 'on' : 'off'}
      </PillButton>
      <PillButton
        onClick={() => clearHighlights()}
        disabled={!hasAny}
        icon={<Trash2 className="h-3 w-3" />}
      >
        Clear
      </PillButton>

      <span className="h-4 w-px bg-border-subtle" aria-hidden />

      {selectedNodeLabel ? (
        <span className="flex items-center gap-1.5">
          <span className="text-text-muted">Selected</span>
          <span className="rounded bg-elevated px-1.5 py-0.5 text-text-primary">
            {selectedNodeLabel}
          </span>
        </span>
      ) : (
        <span className="text-text-muted">No selection</span>
      )}
    </footer>
  );
}

function HighlightCounts({
  highlights,
}: {
  readonly highlights: { citation: string[]; tool: string[]; blast: string[] };
}): JSX.Element {
  return (
    <span className="inline-flex items-center gap-2.5">
      <Dot color={HIGHLIGHT_COLORS.citation} count={highlights.citation.length} label="cit" />
      <Dot color={HIGHLIGHT_COLORS.tool} count={highlights.tool.length} label="tool" />
      <Dot color={HIGHLIGHT_COLORS.blast} count={highlights.blast.length} label="blast" />
    </span>
  );
}

function Dot({
  color,
  count,
  label,
}: {
  readonly color: string;
  readonly count: number;
  readonly label: string;
}): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={cn(
          'h-2 w-2 rounded-full transition-opacity',
          count > 0 ? 'opacity-100' : 'opacity-30',
        )}
        style={{
          background: color,
          boxShadow: count > 0 ? `0 0 6px ${color}` : 'none',
        }}
      />
      <span className={count > 0 ? 'text-text-primary' : 'text-text-muted'}>
        {count} {label}
      </span>
    </span>
  );
}

function Indicator({ running }: { readonly running: boolean }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={cn('h-2 w-2 rounded-full', running ? 'bg-sky-400' : 'bg-border-default')}
        style={running ? { boxShadow: '0 0 6px #38bdf8' } : undefined}
      />
      <span className={running ? 'text-text-primary' : 'text-text-muted'}>
        {running ? 'Layout running…' : 'Layout settled'}
      </span>
    </span>
  );
}

interface PillButtonProps {
  readonly onClick: () => void;
  readonly disabled?: boolean;
  readonly icon?: React.ReactNode;
  readonly title?: string;
  readonly children: React.ReactNode;
}

function PillButton({ onClick, disabled, icon, title, children }: PillButtonProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        'inline-flex h-6 items-center gap-1.5 rounded-md border border-border-subtle px-2 text-[11px] transition-colors',
        disabled
          ? 'cursor-not-allowed opacity-40'
          : 'cursor-pointer hover:border-border-default hover:bg-hover hover:text-text-primary',
      )}
    >
      {icon}
      {children}
    </button>
  );
}

function fmt(n: number | undefined): string {
  return n === undefined ? '—' : n.toLocaleString();
}
